import { useContext, useState } from "react";
import { AppContext } from "../App";
import { callApi } from "../Helpers";
import { contactDetails, companyInfo } from "../data/siteContent";
import { FaEnvelope, FaLocationDot, FaPhoneVolume } from "react-icons/fa6";

export default function ContactUsContent() {
  const appContext = useContext(AppContext);
  const icons = [<FaPhoneVolume />, <FaEnvelope />, <FaLocationDot />];
  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    email: "",
    phone: "",
    services: [],
    otherService: "",
    projectLocation: "",
    projectDescription: "",
    additionalComments: "",
    document: null,
  });
  const [submitting, setSubmitting] = useState(false);

  const availableServices = [
    "Geolocation & Mapping",
    "Land Surveying & Topographical Mapping",
    "High-Resolution Satellite Imagery",
    "Drone/UAV & LiDAR Surveying",
    "GIS & Spatial Data Analysis",
  ];

  function updateField(event) {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function handleServiceChange(event) {
    const { value, checked } = event.target;

    setFormData((current) => {
      let newServices = [...current.services];

      if (checked) {
        if (!newServices.includes(value)) {
          newServices.push(value);
        }
      } else {
        newServices = newServices.filter((s) => s !== value);
      }

      return {
        ...current,
        services: newServices,
        ...(value === "Other" && !checked ? { otherService: "" } : {}),
      };
    });
  }

  function handleFileChange(event) {
    setFormData((current) => ({
      ...current,
      document: event.target.files[0],
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);

    const services = formData.services
      .filter((service) => service !== "Other")
      .concat(
        formData.services.includes("Other") && formData.otherService.trim()
          ? [formData.otherService.trim()]
          : [],
      );

    const payload = {
      fullname: formData.fullName,
      company: formData.companyName,
      email: formData.email,
      phone: formData.phone,
      services: services.join(", "),
      projectLocation: formData.projectLocation,
      projectDescription: formData.projectDescription,
      additionalComments: formData.additionalComments,
    };

    if (formData.document) {
      payload.document = formData.document;
    }

    await callApi("create-messages", payload).then((response) => {
      if (response.status === 1) {
        appContext.tellMessage("Your request has been submitted successfully.");

        setFormData({
          fullName: "",
          companyName: "",
          email: "",
          phone: "",
          services: [],
          otherService: "",
          projectLocation: "",
          projectDescription: "",
          additionalComments: "",
          document: null,
        });

        document.getElementById("file-upload").value = "";
      } else {
        appContext.tellError(response.msg || "Could not submit request.");
      }
    });

    setSubmitting(false);
  }

  return (
    <section className="ags-section">
      <div className="container">
        <div className="row g-4 mb-5 d-md-none">
          {contactDetails.map((item, index) => (
            <div
              className="col-md-4"
              key={item.label}
              data-aos="fade-up"
              data-aos-delay={index * 40}
            >
              <a
                className="ags-contact-card text-decoration-none h-100"
                href={item.href}
                target={item.href.startsWith("http") ? "_blank" : undefined}
                rel={
                  item.href.startsWith("http")
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                <div className="ags-contact-icon mb-3">{icons[index]}</div>
                <p className="ags-section-kicker mb-2">{item.label}</p>
                <h5 className="font-bold text-dark">{item.value}</h5>
              </a>
            </div>
          ))}
        </div>

        <div className="row g-4">
          <div className="col-lg-5" data-aos="fade-right">
            <div className="ags-light-panel d-none d-md-block">
              {contactDetails.map((item, index) => (
                <div
                  className="col-md-4 w-100 mt-3"
                  key={item.label}
                  data-aos="fade-up"
                  data-aos-delay={index * 40}
                >
                  <a
                    className="ags-contact-card text-decoration-none h-100"
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={
                      item.href.startsWith("http")
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    <div className="ags-contact-icon mb-3">{icons[index]}</div>
                    <p className="ags-section-kicker mb-2">{item.label}</p>
                    <h5 className="font-bold text-dark">{item.value}</h5>
                  </a>
                </div>
              ))}
            </div>
            <div className="ags-dark-panel mt-5">
              <p className="ags-section-kicker text-secondary mb-3">
                Headquarters & Branches
              </p>
              <h2 className="section-title text-white mb-4">
                Start the conversation around your next survey or geospatial
                project
              </h2>
              <p className="text-white-50 mb-4">
                AGS serves clients from Dar es Salaam with operational presence
                in Arusha and Kilimanjaro. Reach out for field surveys, drone
                work, GIS implementation, land projects, remote sensing, or
                mapping support.
              </p>
              <div className="row g-3">
                {companyInfo.branches.map((branch) => (
                  <div className="col-sm-4" key={branch}>
                    <div className="ags-branch-pill">{branch}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-7" data-aos="fade-left">
            <div className="ags-surface-panel h-100">
              <div className="row g-4 h-100">
                <div className="">
                  <p className="ags-section-kicker mb-2">Request a Quote</p>
                  <p className="text-muted mb-4">
                    Please complete the form below and our team will contact you
                    to discuss your project requirements and provide a
                    customized quotation.
                  </p>
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="mb-2 fw-bold">Full Name *</label>
                      <input
                        type="text"
                        className="form-control ags-form-control"
                        name="fullName"
                        value={formData.fullName}
                        onChange={updateField}
                        placeholder="Enter full name"
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="mb-2 fw-bold">
                        Organization / Company
                      </label>
                      <input
                        type="text"
                        className="form-control ags-form-control"
                        name="companyName"
                        value={formData.companyName}
                        onChange={updateField}
                        placeholder="Organization or Company Name"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="mb-2 fw-bold">Email Address *</label>
                      <input
                        type="email"
                        className="form-control ags-form-control"
                        name="email"
                        value={formData.email}
                        onChange={updateField}
                        placeholder="Enter email address"
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="mb-2 fw-bold">Phone Number *</label>
                      <input
                        type="tel"
                        className="form-control ags-form-control"
                        name="phone"
                        value={formData.phone}
                        onChange={updateField}
                        placeholder="Enter Phone Number"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="mb-2 fw-bold d-block">
                        Service required *
                      </label>
                      <div className="row g-2">
                        {availableServices.map((service) => (
                          <div className="col-md-6" key={service}>
                            <div className="">
                              <input
                                className=""
                                type="checkbox"
                                value={service}
                                id={`service-${service}`}
                                checked={formData.services.includes(service)}
                                onChange={handleServiceChange}
                              />
                              <label
                                className="form-check-label ms-2"
                                htmlFor={`service-${service}`}
                              >
                                {service}
                              </label>
                            </div>
                          </div>
                        ))}
                        <div className="col-12 mt-2">
                          <div className="form-check d-flex align-items-center gap-2">
                            <input
                              className=" mt-0"
                              type="checkbox"
                              value="Other"
                              id="service-other"
                              checked={formData.services.includes("Other")}
                              onChange={handleServiceChange}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="service-other"
                            >
                              Other:
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm w-auto"
                              name="otherService"
                              value={formData.otherService}
                              onChange={updateField}
                              placeholder="Please specify"
                              disabled={!formData.services.includes("Other")}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="mb-2 fw-bold">Project Location *</label>
                      <input
                        type="text"
                        className="form-control ags-form-control"
                        name="projectLocation"
                        value={formData.projectLocation}
                        onChange={updateField}
                        placeholder="Enter project location"
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="mb-2 fw-bold">
                        Project Description *
                      </label>
                      <textarea
                        className="form-control ags-form-control"
                        name="projectDescription"
                        rows="4"
                        value={formData.projectDescription}
                        onChange={updateField}
                        placeholder="Detail your project requirements"
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="mb-2 fw-bold">
                        Upload Supporting Documents (Optional)
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        className="form-control ags-form-control"
                        name="document"
                        onChange={handleFileChange}
                      />
                    </div>

                    <div className="mb-4">
                      <label className="mb-2 fw-bold">
                        Additional Comments
                      </label>
                      <textarea
                        className="form-control ags-form-control"
                        name="additionalComments"
                        rows="3"
                        value={formData.additionalComments}
                        onChange={updateField}
                        placeholder="Any other details we should know?"
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary btn-rounded btn-padded w-100"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit Request"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
